import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { userAPI, analyticsAPI } from '../services/api';
import { TrendingUp, Award, DollarSign, Target, Clock, AlertTriangle, 
  CheckCircle, Gift, ShoppingBag, FileText, Send, Compass, 
  ChevronRight, Activity, Zap } from 'lucide-react';
import OnboardingChecklist from '../components/OnboardingChecklist';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => userAPI.getProfile().then(res => res.data.user)
  });

  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => userAPI.getStats().then(res => res.data)
  });

  const { data: leaderboard } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => analyticsAPI.getLeaderboard({ limit: 10 }).then(res => res.data.leaderboard)
  });

  const { data: tasksDue } = useQuery({
    queryKey: ['tasks-due'],
    queryFn: () => userAPI.getTasksDue({ limit: 5 }).then(res => res.data)
  });

  const { data: activity } = useQuery({
    queryKey: ['my-activity'],
    queryFn: () => userAPI.getActivity({ limit: 8 }).then(res => res.data.activity)
  });

  const { data: chartData } = useQuery({
    queryKey: ['my-charts'],
    queryFn: () => userAPI.getCharts({ days: 30 }).then(res => res.data.chart_data)
  });

  // Helper: time ago string
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // Helper: days until
  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr).getTime() - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  // Helper: priority color
  const priorityColor = (priority) => {
    const colors = { urgent: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
    return colors[priority] || '#6b7280';
  };

  // Activity icon per type
  const activityIcon = (type) => {
    switch (type) {
      case 'task_completed': return <CheckCircle size={16} />;
      case 'points_earned': return <Zap size={16} />;
      case 'points_spent': return <ShoppingBag size={16} />;
      case 'reward_redeemed': return <Gift size={16} />;
      case 'sale_earned': return <DollarSign size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const activityColor = (type) => {
    switch (type) {
      case 'task_completed': return '#10b981';
      case 'points_earned': return '#6366f1';
      case 'points_spent': return '#f59e0b';
      case 'reward_redeemed': return '#ec4899';
      case 'sale_earned': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Chart helpers
  const maxChartValue = (data, key) => {
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map(d => d[key] || 0), 1);
  };

  // Last 14 days for mini chart display
  const recentChartData = chartData?.slice(-14) || [];

  return (
    <div className="dashboard">
      <OnboardingChecklist />
      <h1>Welcome back, {profile?.first_name}!</h1>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e0e7ff' }}>
            <Award size={24} color="#6366f1" />
          </div>
          <div className="stat-info">
            <div className="stat-value">{profile?.points || 0}</div>
            <div className="stat-label">Total Points</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dcfce7' }}>
            <Target size={24} color="#10b981" />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats?.tasks?.completed || 0}</div>
            <div className="stat-label">Tasks Completed</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>
            <DollarSign size={24} color="#f59e0b" />
          </div>
          <div className="stat-info">
            <div className="stat-value">${stats?.sales?.commissions?.toFixed(2) || '0.00'}</div>
            <div className="stat-label">Total Earnings</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fce7f3' }}>
            <TrendingUp size={24} color="#ec4899" />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats?.sales?.count || 0}</div>
            <div className="stat-label">Sales Generated</div>
          </div>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div className="quick-actions-row">
        <button className="quick-action-card" onClick={() => navigate('/track-post')}>
          <div className="qa-icon" style={{ background: '#ede9fe' }}>
            <Send size={20} color="#7c3aed" />
          </div>
          <div className="qa-text">
            <div className="qa-title">Track a Post</div>
            <div className="qa-desc">Submit a social media post</div>
          </div>
          <ChevronRight size={18} className="qa-arrow" />
        </button>

        <button className="quick-action-card" onClick={() => navigate('/tasks')}>
          <div className="qa-icon" style={{ background: '#dbeafe' }}>
            <FileText size={20} color="#3b82f6" />
          </div>
          <div className="qa-text">
            <div className="qa-title">View Tasks</div>
            <div className="qa-desc">See available tasks</div>
          </div>
          <ChevronRight size={18} className="qa-arrow" />
        </button>

        <button className="quick-action-card" onClick={() => navigate('/campaigns')}>
          <div className="qa-icon" style={{ background: '#dcfce7' }}>
            <Compass size={20} color="#10b981" />
          </div>
          <div className="qa-text">
            <div className="qa-title">Browse Campaigns</div>
            <div className="qa-desc">Join active campaigns</div>
          </div>
          <ChevronRight size={18} className="qa-arrow" />
        </button>

        <button className="quick-action-card" onClick={() => navigate('/rewards')}>
          <div className="qa-icon" style={{ background: '#fce7f3' }}>
            <Gift size={20} color="#ec4899" />
          </div>
          <div className="qa-text">
            <div className="qa-title">Redeem Rewards</div>
            <div className="qa-desc">Spend your points</div>
          </div>
          <ChevronRight size={18} className="qa-arrow" />
        </button>
      </div>

      {/* Tasks Due Soon + Recent Activity */}
      <div className="dashboard-widgets-row">
        {/* Tasks Due Soon Widget */}
        <div className="dashboard-card widget-card">
          <div className="widget-header">
            <h2><Clock size={20} /> Tasks Due Soon</h2>
            <button className="widget-link" onClick={() => navigate('/tasks')}>View All</button>
          </div>
          
          {tasksDue?.overdue?.length > 0 && (
            <div className="overdue-section">
              <div className="overdue-label"><AlertTriangle size={14} /> Overdue</div>
              {tasksDue.overdue.map(task => (
                <div key={task.id} className="task-due-item overdue" onClick={() => navigate('/tasks')}>
                  <div className="tdi-priority" style={{ background: priorityColor(task.priority) }} />
                  <div className="tdi-info">
                    <div className="tdi-title">{task.title}</div>
                    <div className="tdi-meta">
                      {task.campaign_title && <span>{task.campaign_title}</span>}
                      <span className="tdi-points">{task.points} pts</span>
                    </div>
                  </div>
                  <div className="tdi-due overdue-text">
                    {Math.abs(daysUntil(task.due_date))}d overdue
                  </div>
                </div>
              ))}
            </div>
          )}

          {tasksDue?.upcoming?.length > 0 ? (
            <div className="upcoming-tasks">
              {tasksDue.upcoming.map(task => {
                const days = daysUntil(task.due_date);
                return (
                  <div key={task.id} className="task-due-item" onClick={() => navigate('/tasks')}>
                    <div className="tdi-priority" style={{ background: priorityColor(task.priority) }} />
                    <div className="tdi-info">
                      <div className="tdi-title">{task.title}</div>
                      <div className="tdi-meta">
                        {task.campaign_title && <span>{task.campaign_title}</span>}
                        <span className="tdi-points">{task.points} pts</span>
                      </div>
                    </div>
                    <div className={`tdi-due ${days <= 2 ? 'due-soon' : ''}`}>
                      {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            !tasksDue?.overdue?.length && (
              <div className="widget-empty">
                <CheckCircle size={32} color="#10b981" />
                <p>No upcoming deadlines</p>
              </div>
            )
          )}
        </div>

        {/* Recent Activity Feed */}
        <div className="dashboard-card widget-card">
          <div className="widget-header">
            <h2><Activity size={20} /> Recent Activity</h2>
          </div>

          {activity?.length > 0 ? (
            <div className="activity-feed">
              {activity.map((item, idx) => (
                <div key={`${item.id}-${idx}`} className="activity-item">
                  <div className="ai-icon" style={{ background: `${activityColor(item.type)}20`, color: activityColor(item.type) }}>
                    {activityIcon(item.type)}
                  </div>
                  <div className="ai-content">
                    <div className="ai-title">{item.title}</div>
                    <div className="ai-desc">{item.description}</div>
                  </div>
                  <div className="ai-right">
                    <div className="ai-detail" style={{ color: activityColor(item.type) }}>{item.detail}</div>
                    <div className="ai-time">{timeAgo(item.time)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="widget-empty">
              <Activity size={32} color="#6b7280" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Personal Mini Charts */}
      <div className="dashboard-charts-row">
        <div className="dashboard-card mini-chart-card">
          <h2><TrendingUp size={18} /> Points (Last 14 Days)</h2>
          <div className="mini-chart">
            {recentChartData.length > 0 ? (
              <div className="bar-chart-mini">
                {recentChartData.map((d, i) => {
                  const max = maxChartValue(recentChartData, 'earned');
                  const height = Math.max(((d.earned || 0) / max) * 100, 2);
                  return (
                    <div key={i} className="bar-col-mini" title={`${d.date}: ${d.earned} earned`}>
                      <div className="bar-mini earned" style={{ height: `${height}%` }} />
                      <div className="bar-label-mini">{new Date(d.date).getDate()}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="widget-empty small">
                <p>No data yet</p>
              </div>
            )}
          </div>
          <div className="chart-legend-mini">
            <span><span className="legend-dot earned" /> Points Earned</span>
          </div>
        </div>

        <div className="dashboard-card mini-chart-card">
          <h2><DollarSign size={18} /> Earnings (Last 14 Days)</h2>
          <div className="mini-chart">
            {recentChartData.length > 0 ? (
              <div className="bar-chart-mini">
                {recentChartData.map((d, i) => {
                  const max = maxChartValue(recentChartData, 'earnings');
                  const height = Math.max(((d.earnings || 0) / max) * 100, 2);
                  return (
                    <div key={i} className="bar-col-mini" title={`${d.date}: $${(d.earnings || 0).toFixed(2)}`}>
                      <div className="bar-mini earnings" style={{ height: `${height}%` }} />
                      <div className="bar-label-mini">{new Date(d.date).getDate()}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="widget-empty small">
                <p>No data yet</p>
              </div>
            )}
          </div>
          <div className="chart-legend-mini">
            <span><span className="legend-dot earnings" /> Commissions</span>
          </div>
        </div>

        <div className="dashboard-card mini-chart-card">
          <h2><Target size={18} /> Tasks Completed (Last 14 Days)</h2>
          <div className="mini-chart">
            {recentChartData.length > 0 ? (
              <div className="bar-chart-mini">
                {recentChartData.map((d, i) => {
                  const max = maxChartValue(recentChartData, 'tasks');
                  const height = Math.max(((d.tasks || 0) / max) * 100, 2);
                  return (
                    <div key={i} className="bar-col-mini" title={`${d.date}: ${d.tasks} tasks`}>
                      <div className="bar-mini tasks" style={{ height: `${height}%` }} />
                      <div className="bar-label-mini">{new Date(d.date).getDate()}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="widget-empty small">
                <p>No data yet</p>
              </div>
            )}
          </div>
          <div className="chart-legend-mini">
            <span><span className="legend-dot tasks" /> Tasks Done</span>
          </div>
        </div>
      </div>

      {/* Leaderboard + Quick Stats */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h2>Leaderboard</h2>
          <div className="leaderboard-list">
            {leaderboard?.map((ambassador, index) => (
              <div key={ambassador.id} className="leaderboard-item">
                <div className="rank">#{index + 1}</div>
                <div className="ambassador-avatar">
                  {ambassador.first_name[0]}{ambassador.last_name[0]}
                </div>
                <div className="ambassador-info">
                  <div className="ambassador-name">
                    {ambassador.first_name} {ambassador.last_name}
                  </div>
                  <div className="ambassador-tier">{ambassador.tier}</div>
                </div>
                <div className="points">{ambassador.points} pts</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="dashboard-card">
          <h2>Quick Stats</h2>
          <div className="quick-stats">
            <div className="quick-stat">
              <span>Social Posts</span>
              <strong>{stats?.social?.posts || 0}</strong>
            </div>
            <div className="quick-stat">
              <span>Total Likes</span>
              <strong>{stats?.social?.likes || 0}</strong>
            </div>
            <div className="quick-stat">
              <span>Total Clicks</span>
              <strong>{stats?.social?.clicks || 0}</strong>
            </div>
            <div className="quick-stat">
              <span>Revenue Generated</span>
              <strong>${stats?.sales?.revenue?.toFixed(2) || '0.00'}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
